import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCustomers } from '@/services/customers';

import { parseDatabaseError } from '@/lib/errors';
import { Customer } from '@/types/models';
import { TableSkeleton } from '@/components/common/Skeleton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { Modal } from '@/components/common/Modal';
import { CustomerForm } from '@/features/customers/CustomerForm';
import { CustomerDetail } from '@/features/customers/CustomerDetail';
import { Users, Plus, Phone, Mail } from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

export const CustomersPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [archivedFilter, setArchivedFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [page, setPage] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  // Debounce search to avoid hammering Supabase on every keystroke
  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    setPage(0);
    const timer = setTimeout(() => setDebouncedSearch(val), 400);
    return () => clearTimeout(timer);
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['customers', 'list', { search: debouncedSearch, page, archivedFilter }],
    queryFn: () => fetchCustomers({ search: debouncedSearch, page, archivedFilter }),
    placeholderData: (prev) => prev,
  });

  const customers = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-black flex items-center space-x-2">
            <Users className="w-5 h-5 text-brand-green" />
            <span>Customers</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {totalCount > 0 ? `${totalCount} customer${totalCount !== 1 ? 's' : ''}` : 'Manage your customer directory'}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm shrink-0"
          id="add-customer-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Add Customer</span>
        </button>
      </div>

      {/* Search & Filter Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1 max-w-md">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name, phone or email..."
          />
        </div>

        <div className="flex items-center space-x-1 bg-brand-bg p-1 rounded-lg border border-brand-border text-xs font-semibold">
          <button
            type="button"
            onClick={() => { setArchivedFilter('active'); setPage(0); }}
            className={`px-3 py-1.5 rounded-md transition-colors ${
              archivedFilter === 'active' ? 'bg-white text-brand-black shadow-xs font-bold' : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => { setArchivedFilter('archived'); setPage(0); }}
            className={`px-3 py-1.5 rounded-md transition-colors ${
              archivedFilter === 'archived' ? 'bg-amber-100 text-amber-900 shadow-xs font-bold' : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            Archived
          </button>
          <button
            type="button"
            onClick={() => { setArchivedFilter('all'); setPage(0); }}
            className={`px-3 py-1.5 rounded-md transition-colors ${
              archivedFilter === 'all' ? 'bg-white text-brand-black shadow-xs font-bold' : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <ErrorAlert
          message={parseDatabaseError(error)}
          onRetry={() => refetch()}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-brand-border shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-bg border-b border-brand-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden sm:table-cell">
                  Phone
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden md:table-cell">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden lg:table-cell">
                  Address
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>

            {isLoading ? (
              <TableSkeleton rows={8} cols={5} />
            ) : customers.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-brand-green" />
                      </div>
                      <p className="text-base font-bold text-brand-black">
                        {debouncedSearch ? 'No customers found' : 'No customers yet'}
                      </p>
                      <p className="text-xs text-brand-muted mt-1 max-w-xs">
                        {debouncedSearch
                          ? `No results for "${debouncedSearch}". Try a different search term.`
                          : 'Add your first customer to get started.'}
                      </p>
                      {!debouncedSearch && (
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add First Customer</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-brand-border">
                {customers.map((customer: Customer) => (
                  <tr
                    key={customer.id}
                    onClick={() => setSelectedCustomerId(customer.id)}
                    className="hover:bg-brand-bg cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-brand-green text-white flex items-center justify-center text-xs font-bold uppercase shrink-0">
                          {customer.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-brand-black">{customer.name}</span>
                            {customer.archived_at && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9px] font-bold rounded">
                                Archived
                              </span>
                            )}
                          </div>
                          {customer.customer_code && (
                            <span className="text-[10px] text-brand-muted font-mono">{customer.customer_code}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden sm:table-cell">
                      {customer.phone ? (
                        <span className="inline-flex items-center space-x-1">
                          <Phone className="w-3 h-3" />
                          <span>{customer.phone}</span>
                        </span>
                      ) : (
                        <span className="text-brand-border">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden md:table-cell">
                      {customer.email ? (
                        <span className="inline-flex items-center space-x-1">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[180px]">{customer.email}</span>
                        </span>
                      ) : (
                        <span className="text-brand-border">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden lg:table-cell">
                      <span className="truncate max-w-[200px] block">
                        {customer.address || <span className="text-brand-border">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-brand-green font-semibold hover:underline">
                        View →
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        <Pagination
          page={page}
          totalCount={totalCount}
          onPageChange={(p) => setPage(p)}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <Modal isOpen title="Add Customer" onClose={() => setShowAddModal(false)}>
          <CustomerForm
            onSuccess={() => setShowAddModal(false)}
            onCancel={() => setShowAddModal(false)}
          />
        </Modal>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomerId && (
        <Modal
          isOpen
          title="Customer Details"
          onClose={() => setSelectedCustomerId(null)}
          maxWidth="lg"
        >
          <CustomerDetail
            customerId={selectedCustomerId}
            onClose={() => setSelectedCustomerId(null)}
          />
        </Modal>
      )}
    </div>
  );
};
